<template>
  <div class="flex justify-end">
    <!-- Icon-only trigger (ellipsis) for page headers (MAI-4951). -->
    <!-- Secondary (gray) per MAI-4984 — matches the gray back button. -->
    <Button
      v-if="iconOnly"
      data-testid="actions-menu-button"
      type="button"
      icon="pi pi-ellipsis-h"
      severity="secondary"
      text
      rounded
      aria-label="Actions"
      @click="toggleMenu"
    />
    <Button
      v-else
      data-testid="actions-menu-button"
      type="button"
      label="Actions"
      icon="pi pi-chevron-down"
      icon-pos="right"
      class="!bg-gray-500 !border-gray-600 hover:!bg-gray-600 focus:!border-gray-400 active:!border-gray-400 text-sm"
      @click="toggleMenu"
    />
    <!-- TieredMenu only when an item has children; the item markup is shared. -->
    <component
      :is="menuComponent"
      ref="menu"
      :model="computedMenuItems"
      :popup="true"
    >
      <template #item="{ item, hasSubmenu }">
        <hr v-if="item.isSeparator" class="my-2 border-t border-gray-200" />
        <!-- Enter/Space in Menu and TieredMenu click the focused row's
             `a[data-pc-section="itemlink"]`; without it the keyboard path
             clicks the bare `li` and submenu children never run. -->
        <a
          v-else
          v-tooltip.left="
            item.disabled && item.disabledTooltip
              ? {
                  value: item.disabledTooltip,
                  autoHide: false,
                }
              : undefined
          "
          class="flex items-center p-2 cursor-pointer hover:!bg-gray-100"
          :class="[
            {
              'pointer-events-auto': item.disabled && item.disabledTooltip,
              'text-red-500': item.dangerous,
            },
            disabledClass(item),
            item.class,
          ]"
          v-bind="item.dataTestId ? { 'data-testid': item.dataTestId } : {}"
          data-pc-section="itemlink"
          @click="onItemClick($event, item)"
        >
          <i :class="[item.icon, 'mr-2']"></i>
          <span class="text-sm">{{ item.label }}</span>
          <i v-if="hasSubmenu" class="pi pi-angle-right ml-auto pl-4"></i>
        </a>
      </template>
    </component>
  </div>
</template>

<script lang="ts">
/** Divider row; pass it inside `menuItems` to split the menu into sections. */
export const ACTIONS_MENU_SEPARATOR = {
  label: "Separator",
  icon: "pi pi-fw",
  command: () => {},
  isSeparator: true as const,
};
</script>

<script setup lang="ts">
import Button from "primevue/button";
import Menu from "primevue/menu";
import TieredMenu from "primevue/tieredmenu";
import Tooltip from "primevue/tooltip";
import type { MenuItem as PrimeMenuItem } from "primevue/menuitem";
import { ref, computed, type VNode } from "vue";

const vTooltip = Tooltip;

interface MenuItem extends PrimeMenuItem {
  label: string;
  icon: string;
  command: () => void;
  disabled?: boolean;
  isSeparator?: boolean;
  dataTestId?: string;
  disabledTooltip?: string | null;
  class?: string;
  disabledClass?: string;
  /** Children open as a submenu ("Add registration ›"). */
  items?: MenuItem[];
}

interface DangerousAction extends MenuItem {
  dangerous: true;
}

const props = defineProps<{
  menuItems: MenuItem[];
  dangerousAction?: DangerousAction;
  dangerousActions?: DangerousAction[];
  /** Render as a bare ellipsis icon button instead of the labeled button. */
  iconOnly?: boolean;
}>();

const menu = ref();

const separatorItem: MenuItem = ACTIONS_MENU_SEPARATOR;

const disabledClass = (item: MenuItem) => {
  if (item.disabled) {
    if (item.disabledClass) {
      return item.disabledClass;
    }
    if (item.class) {
      return "opacity-50";
    } else {
      return "opacity-50 cursor-not-allowed";
    }
  }
  return "";
};

const hasNested = computed(() =>
  props.menuItems.some((item) => item.items && item.items.length > 0)
);

// The surface both components share; typing the dynamic component this way
// gives the #item slot our MenuItem plus TieredMenu's `hasSubmenu`.
type PopupMenuComponent = new () => {
  $props: { model?: MenuItem[]; popup?: boolean };
  $slots: { item(scope: { item: MenuItem; hasSubmenu?: boolean }): VNode[] };
  toggle(event: Event): void;
};

const menuComponent = computed(
  () => (hasNested.value ? TieredMenu : Menu) as unknown as PopupMenuComponent
);

// TieredMenu expands a submenu on hover only once it is "dirty" (after a
// first click inside it); arm it on open so "Add registration ›" opens on
// hover straight away.
const toggleMenu = (event: Event) => {
  menu.value.toggle(event);
  if (hasNested.value && menu.value.visible) {
    menu.value.dirty = true;
  }
};

// Both Menu and TieredMenu run `command` (and open submenus) from their own
// item wrapper, so the anchor only has to swallow clicks on disabled items.
const onItemClick = (event: Event, item: MenuItem) => {
  if (item.disabled) {
    event.preventDefault();
    event.stopPropagation();
  }
};

const computedMenuItems = computed(() => {
  const allDangerousActions =
    props.dangerousActions ??
    (props.dangerousAction ? [props.dangerousAction] : []);

  if (allDangerousActions.length > 0) {
    const items = [...props.menuItems];
    if (items.length > 0) {
      items.push(separatorItem);
    }
    items.push(...allDangerousActions);
    return items;
  }
  return props.menuItems;
});
</script>
